import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import ActionMenu from '../components/ActionMenu';
import ExportAntennesModal from '../components/ExportAntennesModal';
import { useAuth } from 'react-oidc-context';
import { getAntennes, getAntennesStats, setAuthToken, getEtats, getLieuTypes, exportAntennes } from '../services/api';
import { toast } from 'react-toastify';
import { Gate } from '../lib/perms';
import './AntennesList.css';

const getRelativeTime = (date) => {
  if (!date) return '--';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "à l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays < 30) return `il y a ${diffDays}j`;
  return d.toLocaleDateString('fr-FR');
};

const formatAntenneId = (id) => {
  return `ANT-${String(id).padStart(3, '0')}`;
};

const getTypeClass = (typeName) => {
  if (!typeName) return '';
  const t = typeName.toLowerCase();
  if (t.includes('classik')) return 'type-classik';
  if (t.includes('spherik')) return 'type-spherik';
  if (t.includes('prestige')) return 'type-prestige';
  return 'type-default';
};

const getStatusInfo = (etatNom, statut) => {
  if (etatNom) {
    const e = etatNom.toLowerCase();
    if (e.includes('maintenance')) return { class: 'status-pill-maintenance', label: 'maintenance' };
    if (e.includes('inactif')) return { class: 'status-pill-inactive', label: 'inactive' };
    if (e.includes('actif')) return { class: 'status-pill-active', label: 'active' };
  }
  if (statut === true) return { class: 'status-pill-active', label: 'active' };
  if (statut === false) return { class: 'status-pill-inactive', label: 'inactive' };
  return { class: 'status-pill-active', label: 'active' };
};

function AntennesList() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [antennes, setAntennes] = useState([]);
  const [stats, setStats] = useState(null);
  const [etats, setEtats] = useState([]);
  const [lieuTypes, setLieuTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const lancerExport = async (params) => {
    setExporting(true);
    try {
      await exportAntennes(params);
      toast.success('Export téléchargé');
      setExportOpen(false);
    } catch (e) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
  const [selectedEtat, setSelectedEtat] = useState(searchParams.get('etat') || '');
  const [selectedVille, setSelectedVille] = useState(searchParams.get('ville') || '');

  // Les cartouches filtrent par état ; l'id dépend du référentiel, on le
  // retrouve par le nom plutôt que de coder un identifiant en dur.
  // Le libelle des etats vit dans `valeur`. On cherche par inclusion, les
  // referentiels ecrivant aussi bien « maintenance » que « En maintenance ».
  const etatIdParNom = (motif) => {
    const norm = (x) => String(x?.valeur || x?.nom || x?.libelle || '').toLowerCase();
    const e = (etats || []).find((x) =>
      motif === 'actif'
        ? norm(x).includes('actif') && !norm(x).includes('inactif')
        : norm(x).includes(motif)
    );
    return e ? e.id : null;
  };
  // « En maintenance » n'a pas de critere propre dans les statistiques : on
  // retombe sur l'etat du referentiel quand il existe.
  const idMaintenance = etatIdParNom('maintenance');
  const lienMaintenance = idMaintenance ? `/antennes?etat=${idMaintenance}` : '/antennes';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const page = parseInt(searchParams.get('page')) || 1;

      const [antennesData, statsData] = await Promise.all([
        getAntennes({
          page,
          limit: 20,
          search: searchParams.get('search') || searchParams.get('ville') || '',
          etat_id: searchParams.get('etat') || '',
          lieu_type_id: searchParams.get('type') || '',
          statut: searchParams.get('statut') || ''
        }),
        getAntennesStats()
      ]);

      setAntennes(antennesData.data);
      setPagination(antennesData.pagination);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const loadLookups = async () => {
    try {
      const [etatsData, lieuTypesData] = await Promise.all([
        getEtats(),
        getLieuTypes()
      ]);
      setEtats(etatsData);
      setLieuTypes(lieuTypesData);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadData();
      loadLookups();
    }
  }, [auth.user, loadData]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) params.set('search', search); else params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFilterChange = (setter, value, paramName) => {
    setter(value);
    const params = new URLSearchParams(searchParams);
    if (value) params.set(paramName, value); else params.delete(paramName);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedType('');
    setSelectedEtat('');
    setSelectedVille('');
    setSearchParams({});
  };

  const hasActiveFilters = search || selectedType || selectedEtat || selectedVille;
  const totalMaintenance = Math.max(0, (stats?.total_antennes || 0) - (stats?.total_actives || 0) - (stats?.total_inactives || 0));

  return (
    <div className="antennes-page">
      <header className="page-header">
        <div>
          <h1>Villes principales</h1>
          <p className="page-subtitle">Liste et gestion du réseau</p>
        </div>
        <div className="page-header-actions">
          <Gate perm="antennes.export">
            <button className="k-btn k-btn-secondary" onClick={() => setExportOpen(true)}>Exporter</button>
            {exportOpen && <ExportAntennesModal busy={exporting} onExport={lancerExport} onClose={() => !exporting && setExportOpen(false)} />}
          </Gate>
          <Gate perm="antennes.create">
            <Link to="/antennes/new" className="k-btn k-btn-primary">+ Nouvelle antenne</Link>
          </Gate>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="kpi-grid-4">
        <Link to={'/antennes'} className="kpi-card kpi-total kpi-card-link" title="Voir toutes les antennes">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Total antennes</span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_antennes || 0)}</div>
          </div>
        </Link>
        <Link to="/antennes?statut=true" className="kpi-card kpi-actives kpi-card-link" title="Voir les antennes actives">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Actives</span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_actives || 0)}</div>
          </div>
        </Link>
        <Link to={lienMaintenance} className="kpi-card kpi-maintenance kpi-card-link" title="Voir les antennes en maintenance">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">En maintenance</span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : totalMaintenance}</div>
          </div>
        </Link>
        <Link to="/antennes?statut=false" className="kpi-card kpi-inactives kpi-card-link" title="Voir les antennes inactives">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Inactives</span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_inactives || 0)}</div>
          </div>
        </Link>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <form onSubmit={handleSearch} className="filter-search-form">
          <div className="filter-search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="filter-search-input"
            />
          </div>
        </form>
        <div className="filter-badges">
          <select
            value={selectedType}
            onChange={(e) => handleFilterChange(setSelectedType, e.target.value, 'type')}
            className="filter-badge-select"
          >
            <option value="">Type ▾</option>
            {lieuTypes.map(t => (
              <option key={t.id} value={t.id}>{t.nom}</option>
            ))}
          </select>

          <select
            value={selectedEtat}
            onChange={(e) => handleFilterChange(setSelectedEtat, e.target.value, 'etat')}
            className="filter-badge-select"
          >
            <option value="">Statut ▾</option>
            {etats.map(e => (
              <option key={e.id} value={e.id}>{e.valeur || e.nom}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Ville"
            value={selectedVille}
            onChange={(e) => {
              setSelectedVille(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleFilterChange(setSelectedVille, e.target.value, 'ville');
              }
            }}
            onBlur={(e) => {
              if (e.target.value !== searchParams.get('ville')) {
                handleFilterChange(setSelectedVille, e.target.value, 'ville');
              }
            }}
            className="filter-badge-input"
          />

          {hasActiveFilters && (
            <button type="button" onClick={clearFilters} className="filter-reset-btn">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Table Card */}
      <div className="k-table-card">
        <div className="k-table-card-header">
          <span className="k-table-card-title">Antennes</span>
          <span className="k-table-card-count">{pagination.total} éléments</span>
        </div>

        <div className="k-table-wrapper">
          <table className="k-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Ville</th>
                <th>Total</th>
                <th>Type</th>
                <th>Statut</th>
                <th>Dernier événement</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">
                    <div className="spinner-sm"></div> Chargement...
                  </td>
                </tr>
              ) : antennes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">Aucune antenne trouvée</td>
                </tr>
              ) : (
                antennes.map((antenne) => {
                  const status = getStatusInfo(antenne.etat_nom, antenne.statut);
                  return (
                    <tr key={antenne.id} onClick={() => navigate(`/antennes/${antenne.id}`)}>
                      <td className="td-id">{formatAntenneId(antenne.id)}</td>
                      <td>
                        <Link to={`/antennes/${antenne.id}`} className="td-nom-link" onClick={(e) => e.stopPropagation()}>
                          {antenne.ville_principale || antenne.ville_exacte || '(Non renseigné)'}
                        </Link>
                      </td>
                      <td>{antenne.ville_principale || '--'}</td>
                      <td className="td-total" title="Contacts antennes rattachés">
                        {antenne.nb_contacts ?? 0}
                      </td>
                      <td>
                        {antenne.lieu_type_nom ? (
                          <span className={`type-badge ${getTypeClass(antenne.lieu_type_nom)}`}>
                            {antenne.lieu_type_nom.toLowerCase()}
                          </span>
                        ) : '--'}
                      </td>
                      <td>
                        <span className={`status-pill ${status.class}`}>
                          <span className="status-pill-dot"></span>
                          {status.label}
                        </span>
                      </td>
                      <td className="td-event">{getRelativeTime(antenne.modified)}</td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        {/* Le clic sur la ligne ouvre la fiche : ce menu doit donc
                            arrêter la propagation, sinon les deux se déclenchent. */}
                        <ActionMenu
                          items={[
                            { label: 'Voir la fiche', onClick: () => navigate(`/antennes/${antenne.id}`) },
                            { label: 'Modifier', onClick: () => navigate(`/antennes/${antenne.id}/edit`) },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="k-table-footer">
            <span>{pagination.total} résultat{pagination.total > 1 ? 's' : ''}</span>
            {pagination.total > pagination.limit && (
              <div className="pagination-buttons">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="pagination-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                {Array.from({ length: Math.min(5, Math.ceil(pagination.total / pagination.limit)) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                  className="pagination-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AntennesList;
